/**
 * Secure logging utility for production webapp
 * Only logs in development environment and sanitizes sensitive data
 */

// Sensitive data patterns that should be redacted
const SENSITIVE_PATTERNS = [
  /auth_token/i,
  /refresh_token/i,
  /password/i,
  /api[_-]?key/i,
  /secret/i,
  /token/i,
  /bearer/i,
  /authorization/i,
  /cookie/i,
  /session/i,
  /jwt/i,
  /email/i,
  /user[_-]?id/i,
  /personal[_-]?data/i,
];

// Function to sanitize sensitive data in objects and strings
const sanitizeData = (data) => {
  if (typeof data === 'string') {
    let sanitized = data;
    SENSITIVE_PATTERNS.forEach(pattern => {
      sanitized = sanitized.replace(new RegExp(pattern.source, pattern.flags), '[REDACTED]');
    });
    return sanitized;
  }

  if (data && typeof data === 'object') {
    const sanitized = { ...data };

    // Recursively sanitize nested objects
    Object.keys(sanitized).forEach(key => {
      const lowerKey = key.toLowerCase();

      // Check if key contains sensitive patterns
      const isSensitive = SENSITIVE_PATTERNS.some(pattern => pattern.test(lowerKey));

      if (isSensitive) {
        sanitized[key] = '[REDACTED]';
      } else if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
        sanitized[key] = sanitizeData(sanitized[key]);
      } else if (typeof sanitized[key] === 'string') {
        // Also check string values for sensitive patterns
        let value = sanitized[key];
        SENSITIVE_PATTERNS.forEach(pattern => {
          if (pattern.test(value)) {
            value = value.replace(new RegExp(pattern.source, pattern.flags), '[REDACTED]');
          }
        });
        sanitized[key] = value;
      }
    });

    return sanitized;
  }

  return data;
};

// Only log in development environment
const isDevelopment = process.env.NODE_ENV === 'development';

const logger = {
  info: (...args) => {
    if (isDevelopment) {
      console.log('[INFO]', ...args.map(sanitizeData));
    }
  },

  warn: (...args) => {
    if (isDevelopment) {
      console.warn('[WARN]', ...args.map(sanitizeData));
    }
  },

  error: (...args) => {
    if (isDevelopment) {
      console.error('[ERROR]', ...args.map(sanitizeData));
    }
  },

  debug: (...args) => {
    if (isDevelopment) {
      console.debug('[DEBUG]', ...args.map(sanitizeData));
    }
  },

  // Special method for development-only logging that should be removed in production
  dev: (...args) => {
    if (isDevelopment) {
      console.log('[DEV]', ...args.map(sanitizeData));
    }
  },

  // Method to log API calls safely (removes query parameters, tokens, etc.)
  apiCall: (method, url, options = {}) => {
    if (isDevelopment) {
      const sanitizedUrl = url.replace(/([?&])([^=]+)=([^&]*)/g, (match, sep, key, value) => {
        const lowerKey = key.toLowerCase();
        const isSensitive = SENSITIVE_PATTERNS.some(pattern => pattern.test(lowerKey));
        return isSensitive ? `${sep}${key}=[REDACTED]` : match;
      });

      console.log('[API]', method, sanitizedUrl);
    }
  }
};

export default logger;
